import {useAppSelector} from '@/redux-store/store';
import {fetchOtherUserData} from '@/rest/user/fetch-other-user-data';
import {
  MATRIX_SERVER_URL,
  MATRIX_URL_SUFFIX
} from '@/services/matrix/client/constants';
import {SupportedLanguage} from '@/types/common';
import {MultimediaMessageContent, SYSTEM_MESSAGE_TYPES} from '@/types/matrix';
import {
  isToday,
  isYesterday,
  format,
  parseISO,
  differenceInCalendarDays,
  getYear,
  formatDistanceToNow
} from 'date-fns';
import {enUS, de} from 'date-fns/locale';
import {MatrixEvent} from 'matrix-js-sdk';

interface GroupedMatrixEvents {
  [dateLabel: string]: MatrixEvent[];
}

interface TimeGroupedMatrixEvents {
  [timeLabel: string]: MatrixEvent[];
}

export interface DateAndTimeGroupedMatrixEvents {
  [dateLabel: string]: TimeGroupedMatrixEvents;
}

export const locales: Record<SupportedLanguage, Locale> = {
  de: de,
  en: enUS
};

export const formatTime = (dateString: string): string => {
  const date = parseISO(dateString);
  return format(date, 'HH:mm');
};

export const formatDateByLocale = (
  date: Date,
  locale: Locale,
  t: (key: string) => string,
  isCustomFormat = true,
  showTimeForToday = false
): string => {
  try {
    const currentYear = getYear(new Date());
    const eventYear = getYear(date);
    if (isCustomFormat) {
      const now = new Date();

      if (isToday(date)) {
        const oneMinuteAgo = new Date(now.getTime() - 1 * 60 * 1000);
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

        if (showTimeForToday) {
          if (date > oneMinuteAgo && date <= now) {
            return t('just_now');
          } else if (date > fiveMinutesAgo && date <= oneMinuteAgo) {
            return formatDistanceToNow(date, {locale, addSuffix: true});
          } else {
            return format(date, 'p', {locale});
          }
        }
        return t('today');
      } else if (isYesterday(date)) {
        return t('yesterday');
      } else {
        const daysDifference = differenceInCalendarDays(new Date(), date);
        if (daysDifference > 0 && daysDifference < 7) {
          return format(date, 'EEEE', {locale});
        } else if (daysDifference === 7) {
          return t('one_week_ago');
        } else {
          if (currentYear === eventYear) {
            return format(date, 'd MMM', {locale});
          } else {
            return format(date, 'PP', {locale});
          }
        }
      }
    } else {
      return format(date, 'PP', {locale});
    }
  } catch (error) {
    console.log('error while formatting date', error);
    return date.toLocaleDateString();
  }
};

export const groupMatrixEventByDate = (
  events: MatrixEvent[],
  t: (key: string) => string,
  language: SupportedLanguage
): GroupedMatrixEvents => {
  return events.reduce((acc: GroupedMatrixEvents, event: MatrixEvent) => {
    const originalTimestamp = extractOriginalTimestamp(event);

    const timestampToUse = originalTimestamp || event.event.origin_server_ts;

    if (typeof timestampToUse === 'number') {
      const msgDate = new Date(timestampToUse);
      const dateLabel = formatDateByLocale(msgDate, locales[language], t);

      if (!acc[dateLabel]) {
        acc[dateLabel] = [];
      }

      acc[dateLabel].push(event);
    }

    return acc;
  }, {});
};

export const groupAndSortMatrixEventsByTime = (
  events: MatrixEvent[]
): TimeGroupedMatrixEvents => {
  let lastSenderId: string | null | undefined = null;
  let lastImageId: string | null = null;
  let lastVideoId: string | null = null;
  let groupCounter = 0;
  let lastTimeLabel: string | null = null;
  let lastEventSouldInNewGroup = false;
  let additionalTag = null;
  let lastEventMessageType: string | null | undefined = null;

  const sortedEvents = events.sort((a, b) => {
    const timestampA =
      extractOriginalTimestamp(a) || a.event.origin_server_ts || 0;
    const timestampB =
      extractOriginalTimestamp(b) || b.event.origin_server_ts || 0;
    return timestampA - timestampB;
  });

  return sortedEvents.reduce(
    (acc: TimeGroupedMatrixEvents, event: MatrixEvent) => {
      const originalTimestamp = extractOriginalTimestamp(event);
      const timestampToUse = originalTimestamp || event.event.origin_server_ts;
      const currentEventMessageType = event.getEffectiveEvent().content.msgtype;
      const isQuoteReplyEvent = isQuoteReply(event);
      const isEventSystemMessage = isSystemMessage(event);
      const isEventImageMessage = isImageMessage(event);
      const isEventVideoMessage = isVideoMessage(event);
      additionalTag = isEventImageMessage
        ? '-image'
        : isEventVideoMessage
        ? '-video'
        : '';
      const imageId = isEventImageMessage
        ? event.event.content?.info.groupId
        : null;

      const videoId = isEventVideoMessage
        ? event.event.content?.info.groupId
        : null;

      if (typeof timestampToUse === 'number') {
        const senderId = event.event.sender;
        const timeLabel = format(new Date(timestampToUse), 'HH:mm');

        if (lastTimeLabel !== timeLabel) {
          groupCounter = 0;
          lastTimeLabel = timeLabel;
        }

        if (
          lastSenderId !== senderId ||
          isQuoteReplyEvent ||
          isEventSystemMessage ||
          (isEventImageMessage && lastImageId !== imageId) ||
          (isEventVideoMessage && lastVideoId !== videoId) ||
          lastEventMessageType !== currentEventMessageType
        ) {
          groupCounter++;
          lastSenderId = senderId;
          lastImageId = imageId;
          lastVideoId = videoId;
          lastEventMessageType = currentEventMessageType;
        }

        if (lastEventSouldInNewGroup) {
          groupCounter++;
        }

        lastEventSouldInNewGroup =
          isQuoteReplyEvent ||
          isEventSystemMessage ||
          (isEventImageMessage && lastImageId !== imageId) ||
          (isEventVideoMessage && lastVideoId !== videoId);

        const groupKey = `${timeLabel}-${groupCounter}${additionalTag}`;

        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }

        acc[groupKey].push(event);

        acc[groupKey].sort((a, b) => {
          const timestampA =
            extractOriginalTimestamp(a) || a.event.origin_server_ts || 0;
          const timestampB =
            extractOriginalTimestamp(b) || b.event.origin_server_ts || 0;

          return timestampA - timestampB;
        });
      }

      return acc;
    },
    {}
  );
};

export const getUrlsFromEvents = (content: MultimediaMessageContent) => {
  const urls: string[] = [];

  if (content.url) {
    urls.push(content.url);
  }

  content.additional.forEach((additionalInfo) => {
    if (additionalInfo.url) {
      urls.push(additionalInfo.url);
    }
  });
  const directUrls = urls.map((mxcUrl) => {
    return `${MATRIX_SERVER_URL}/_matrix/media/r0/download/${mxcUrl.substring(
      6
    )}`;
  });

  return directUrls;
};

export const extractOriginalTimestamp = (
  content: MatrixEvent
): number | null => {
  const regexPattern = /Original timestamp: (\d+)/;

  if (
    content.event.type === 'm.room.redaction' &&
    content.event.content?.reason
  ) {
    const reason = content.event.content.reason;
    const match = reason.match(regexPattern);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }
  }
  return null;
};

export function isQuoteReply(event: MatrixEvent): boolean {
  const effectiveEvent = event.getEffectiveEvent();
  const eventContent = effectiveEvent.content.body || '';
  return eventContent.includes('[[QUOTE id=');
}

export const isSystemMessage = (event: MatrixEvent) => {
  const effectiveEvent = event.getEffectiveEvent();
  return SYSTEM_MESSAGE_TYPES.includes(effectiveEvent.type);
};

export const isImageMessage = (event: MatrixEvent) => {
  const effectiveEvent = event.getEffectiveEvent();
  return effectiveEvent.content.msgtype === 'm.image';
};

export const isVideoMessage = (event: MatrixEvent) => {
  const effectiveEvent = event.getEffectiveEvent();
  return effectiveEvent.content.msgtype === 'm.video';
};

export const isGroupOfImages = (events: MatrixEvent[]) => {
  return events[0].getEffectiveEvent().content.msgtype === 'm.image';
};
export function extractEmailPart(input: string | undefined): string | null {
  if (!input) {
    return null;
  }

  const regex = /@([^:]+)/;

  const match = input.match(regex);

  return match ? match[1] : null;
}

export function getProperMailAddress(email: string): string | null {
  const properMail = email?.replace('=40', '@').replace(/(\d+)$/, '');
  return properMail;
}

export const useGetUserId = (matrixUserId: string) => {
  const userMail = extractEmailPart(matrixUserId)
    ?.replace('=40', '@')
    .replace(/(\d+)$/, '');
  const userIdFromRedux = useAppSelector(
    (state) => state.company.employees
  ).find((employee) => userMail === employee.email)?._id;

  return userIdFromRedux;
};

export const makeMatrixUserIdFromUserMail = (userMail: string) => {
  const prefix = userMail.replace('@', '=40');
  const matrixUserId = `@${prefix}:${MATRIX_URL_SUFFIX}`;
  return matrixUserId;
};

export const getUserEmail = async (userId: string) => {
  const userData = await fetchOtherUserData(userId);
  const mail = userData.email;
  return mail;
};

export const filterEventsByRedactions = (
  dateGroup: MatrixEvent[],
  readyRedactedIds: Set<unknown>,
  latelyRedactedMatrixEvents: MatrixEvent[]
) => {
  return dateGroup.filter((event) => {
    const effectiveEvent = event.getEffectiveEvent();
    return !(
      effectiveEvent.type === 'm.room.redaction' &&
      !readyRedactedIds.has(effectiveEvent.content?.redacts) &&
      !latelyRedactedMatrixEvents.some((redactedEvent) => {
        const redactedEffectiveEvent = redactedEvent.getEffectiveEvent();
        return redactedEffectiveEvent.event_id === effectiveEvent.event_id;
      })
    );
  });
};
